import Autolinker from "autolinker";
import { SocialNetwork } from "./SocialNetwork";

/**
 * Turning a post's plain text into the HTML a card renders.
 *
 * Kept apart from apiProvider because it is the part with rules worth testing: Autolinker's
 * "twitter" mode rewrites #tags and @mentions into twitter.com URLs, which is right for a tweet
 * and silently wrong for anything else.
 */

const BLUESKY_PROFILE = "https://bsky.app/profile/";
const BLUESKY_TAG_SEARCH = "https://bsky.app/search?q=%23";

/** Bluesky handles are domains: @someone.bsky.social, @president.europarl.europa.eu. */
const BLUESKY_MENTION = /(^|[\s(])@([a-z0-9][a-z0-9-]*(?:\.[a-z0-9-]+)+)/gi;
const BLUESKY_HASHTAG = /(^|[\s(])#([\p{L}\p{N}_]+)/gu;

/**
 * Plain text as HTML text. A Bluesky post is stored as its author typed it, so "<b>" in it is three
 * characters to show, not markup to build. X sends its text HTML-escaped already, which is why tweets
 * skip this: escaping them again would show "&amp;amp;".
 */
const escapeHtml = (text: string): string =>
    text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const link = (href: string, text: string): string =>
    `<a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>`;

/**
 * Links a post's text for the network it came from. Only Twitter gets Autolinker's twitter mode;
 * Bluesky mentions and tags are linked to bsky.app, and URLs are linked everywhere.
 */
export const linkifyContent = (content: string, source: SocialNetwork): string => {
    if (! content) {
        return "";
    }
    if (source === "twitter") {
        return Autolinker.link(content, { newWindow: true, hashtag: "twitter", mention: "twitter" });
    }

    const urlsLinked = Autolinker.link(escapeHtml(content), { newWindow: true, hashtag: false, mention: false });
    if (source !== "bluesky") {
        return urlsLinked;
    }
    // Applied after Autolinker, so an @handle already inside an <a href> is left alone: the
    // patterns only match at a boundary that a generated tag does not produce.
    return urlsLinked
        .replace(BLUESKY_MENTION, (match, before, handle) =>
            `${before}${link(BLUESKY_PROFILE + handle, "@" + handle)}`)
        .replace(BLUESKY_HASHTAG, (match, before, tag) =>
            `${before}${link(BLUESKY_TAG_SEARCH + tag, "#" + tag)}`);
};

/** Where an account lives on its own network, for linking a reposted-from handle. */
export const profileUrl = (source: SocialNetwork, handle: string): string | undefined => {
    if (! handle) {
        return undefined;
    }
    switch (source) {
        case "bluesky":
            return BLUESKY_PROFILE + handle;
        case "twitter":
            return `https://x.com/${handle}`;
        default:
            return undefined;
    }
};

/** The shortened link X appends to a quote's text, pointing at the quoted tweet. */
const TRAILING_TCO = /\s*https?:\/\/t\.co\/\w+\s*$/;

/**
 * Drops the t.co link X puts at the end of a quote's text. It is the quoted tweet, and the card
 * now says who is quoted and links the post in its own line above the text, so the bare
 * "t.co/BA5DvASd3K" inside the text is the same link with a worse name. Only when the API named
 * the quoted post: without that, the link is the only way to it and must stay.
 */
export const stripQuoteLink = (content: string, postType?: string, quotedUrl?: string): string => {
    if (! content || postType !== "quote" || ! quotedUrl) {
        return content;
    }
    return content.replace(TRAILING_TCO, "");
};

/** "RT @handle: " at the very start, the shape the old ingest wrote into the content. */
const RT_PREFIX = /^RT @[A-Za-z0-9_.\-]+:\s*/;

/**
 * Drops the "RT @handle:" an older ingest put in front of a repost's text. The card names the
 * reposted account in its own header, so repeating it inside the text is noise - and it is display
 * text rather than data, which is why the API also sends repostedFrom.
 *
 * The ingest no longer writes it, but every repost already in the database still carries it, so
 * this stays. Twitter reposts stored before the ingest fix also have no repostedFrom, which is why
 * postType alone is enough to strip: without it those items would keep the prefix forever.
 */
export const stripRepostPrefix = (content: string, repostedFrom?: string,
                                  postType?: string): string => {
    if (! content) {
        return content;
    }
    if (repostedFrom) {
        const prefix = `RT @${repostedFrom}:`;
        if (content.startsWith(prefix)) {
            return content.slice(prefix.length).trimStart();
        }
    }
    return postType === "repost" ? content.replace(RT_PREFIX, "") : content;
};
